import type { World } from '@invaders/sim';
import type { MatchState } from './match';

/** GDD section 10: every map is 1024 × 512 cells, with the sea surface 112 cells above the bottom. */
export const MAP_W = 1024;
export const MAP_H = 512;
export const SEA_LEVEL_FROM_BOTTOM = 112;

/** The simulation runs at 60 ticks per second; durations of the GDD are converted with this. */
export const TICKS_PER_SECOND = 60;

/** Unit positions and velocities: 1 cell = FP units. */
export const FP = 256;
export const FP_SHIFT = 8;
/** Projectiles use a finer fixed point (1 cell = PFP), so that a light wind still bends a trajectory. */
export const PFP = 65536;
export const PFP_SHIFT = 16;

export interface Unit {
  readonly id: number;
  /** Index of the player (team) in lobby order. */
  readonly player: number;
  /** Invader kind (INVADERS index). */
  readonly kind: number;
  /** Top-left corner of the 16 × 16 body box, fixed-point FP. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  alive: boolean;
  facing: 1 | -1;
  walkDir: -1 | 0 | 1;
  walkAcc: number;
  /** Highest row (in cells) reached since last touching the ground. */
  fallStartY: number;
  /** Caught fire this turn: 5 HP of burn at the next resolution (GDD section 6). */
  burning: boolean;
  /** Barbe Noire: ticks before his lit beard blows up, 0 if not lit. */
  fuse: number;
  /** Barbe Noire got wet: his powder no longer risks anything. */
  wet: boolean;
  /**
   * GDD: a frozen unit cannot be played at the next turn of its team. FROZEN_PENDING until that turn starts,
   * FROZEN_NOW during it, then thawed (0).
   */
  frozen: number;
  /** Ticks of sliding left on ice after it stopped walking. */
  slide: number;
  /** Linked to a voodoo doll: it copies the doll's flight (no gravity of its own) until a collision. */
  puppet: boolean;
  /**
   * Jetpack (author's addition): ticks of thrust left, 0 when it is not worn. It is taken off when they run out,
   * when the unit lands after flying, and at the end of the turn.
   */
  jet: number;
  /** Jetpack: the thrust up is held. */
  thrust: boolean;
  /** The double jump was used since the unit left the ground. */
  airJump: boolean;
  /** Drill (author's change): ticks of drilling left, 0 when the unit is not boring its way. */
  drill: number;
  /** Drill: the direction it bores, in integer degrees (0 = right, 90 = up). */
  drillAngle: number;
}

export const FROZEN_PENDING = 1;
export const FROZEN_NOW = 2;

export interface Projectile {
  /** Fixed-point PFP. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** WEAPON_* of weapons.ts. */
  weapon: number;
  /** Unit that fired it; it cannot hit its owner during the first ticks. */
  owner: number;
  age: number;
  /** Voodoo doll: the unit that copies its flight, -1 if none (or the link broke). */
  link: number;
  /** Laser ray: the units it already went through (each takes its damage once). */
  hit?: number[];
  /** Laser ray: flat [x, y, ...] of its start and its bounces (drawn as the beam behind its head). */
  marks?: number[];
  /** Laser ray: its bounces on walls other than ice (RAY_WALL_BOUNCES at most). */
  walls?: number;
}

/**
 * What happened during the last tick, for the client (animations, camera). Derived from the state changes,
 * so not hashed: two clients compute the same events anyway.
 */
export type GameEvent =
  | { type: 'explosion'; x: number; y: number; radius: number }
  | { type: 'shot'; x0: number; y0: number; x1: number; y1: number }
  | { type: 'damage'; unit: number; amount: number }
  | { type: 'death'; unit: number; offMap: boolean }
  /** An ice mine went off under (near) the unit that set it off. */
  | { type: 'iceMine'; unit: number }
  /** A death burst (GDD section 6), at cell (x, y). */
  | { type: 'burst'; unit: number; kind: number; x: number; y: number }
  /** A stele's legacy fired (`stage` 1 to 3), or was cancelled by an explosion (`stage` 0). */
  | { type: 'legacy'; kind: number; x: number; y: number; stage: number }
  | { type: 'launch'; weapon: number; unit: number }
  /** A chest is dropped, opened by a unit (`unit` -1: by an explosion), or lost (in lava). */
  | { type: 'chest'; chest: number; what: 'drop' | 'open' | 'blown' | 'lost'; content: number; unit: number; x: number; y: number }
  | { type: 'drill'; x0: number; y0: number; x1: number; y1: number }
  /** A Roomba swallowing (now and then, for the sound). */
  | { type: 'inhale'; x: number; y: number }
  /** Cold (cryo-grenade, ice mine, Snowman): a disc of radius r froze. */
  | { type: 'freeze'; x: number; y: number; radius: number }
  /** An electric discharge (Robotman): flat [x, y, ...] of some electrified cells, for the sparks. */
  | { type: 'discharge'; x: number; y: number; radius: number; cells: number[] }
  /** A laser ray: flat [x, y, ...] of its path (start, bounce, end). */
  | { type: 'laser'; path: number[] }
  /** A jump pad threw a unit up, from cell (x, y) under its feet. */
  | { type: 'pad'; unit: number; x: number; y: number }
  /** A mimic bit a unit and ran away. */
  | { type: 'bite'; chest: number; unit: number; x: number; y: number }
  /** The remote moved a square: from (sx, sy) to (tx, ty), centres. */
  | { type: 'remote'; sx: number; sy: number; tx: number; ty: number; rot: number }
  /** An ice meteor landed: its block's top-left corner. */
  | { type: 'meteor'; x: number; y: number }
  /** A Wave broke (against a wall, or worn out) at its front. */
  | { type: 'surge'; x: number; y: number; height: number }
  /** Île de Braise: the volcano erupts, from the crater's lava at (x, y). */
  | { type: 'eruption'; x: number; y: number }
  /** The end of the world (author's addition): `count` lava bombs start falling from the sky. */
  | { type: 'stoneRain'; count: number }
  /** Fort-Pierre: the cauldron tipped over the gate; (x, y) under it. */
  | { type: 'cauldron'; x: number; y: number };

/**
 * Where a unit fell (GDD section 6): at the start of each of the next 3 turns it generates the unit's
 * material. A direct explosion on it cancels the rest of the legacy.
 */
export interface Stele {
  /** Cell under the stele's centre: (x, y) is the ground cell it stands on, minus one. */
  x: number;
  y: number;
  readonly kind: number;
  readonly player: number;
  /** Legacies already fired, 0 to 3. */
  stage: number;
}

/** Chest content: a weapon id (WEAPON_*), or CONTENT_MEDKIT. */
export const CONTENT_MEDKIT = 100;

export const CHEST_FALLING = 0;
export const CHEST_GROUND = 1;
/** Opened by a direct explosion: any unit may pick its content during one turn. */
export const CHEST_OPEN = 2;

/** GDD section 9: a treasure chest that comes down by parachute at the start of each turn. */
export interface Chest {
  readonly id: number;
  /** Top-left corner of its box, fixed-point FP. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** CHEST_FALLING (under its parachute), CHEST_GROUND, CHEST_OPEN, or CHEST_SHORE (chests.ts). */
  mode: number;
  readonly content: number;
  /** Ticks spent touching lava (its reinforcements hold 3 s). */
  heat: number;
  /**
   * CHEST_OPEN: last turn its content may be picked (sandbox: last tick). Hopping mimic: target column. A mimic back at
   * rest after running away: the turn (sandbox: the tick) until which it does not bite again.
   */
  until: number;
  /** A mimic running away: hops left before it gives up and hides where it is (0 at rest). */
  hops: number;
  /** GDD: one drop in ten is a mimic, a fake living chest. */
  readonly mimic: boolean;
}

/** GDD section 8: an explosive mine, put or thrown; it arms at the next turn. */
/** A battery (author's addition, battery.ts): it discharges at the start of each of its `turns` turns. */
export interface Battery {
  /** Centre cell. */
  x: number;
  y: number;
  /** The unit that threw it. */
  readonly owner: number;
  turns: number;
  /** Sandbox: ticks since its last discharge. */
  tick: number;
}

export interface Mine {
  /** MINE_EXPLOSIVE or MINE_ICE (mines.ts). */
  readonly kind: number;
  /** Centre cell. */
  x: number;
  y: number;
  /** Turn (match) or tick (sandbox) from which it is armed. */
  readonly arm: number;
  readonly owner: number;
  /** Thrown up by a jump pad: its speed, fixed-point FP per tick (absent or 0: at rest). */
  vy?: number;
  /** Fractions of a cell (FP) travelled and not yet moved: on a conveyor belt (fx), in flight (fy). */
  fx?: number;
  fy?: number;
}

/**
 * La Vague (GDD section 8): a wall of water that runs along the ground from the unit in the aimed direction.
 * Not cells while it runs: the client draws it from these fields; it leaves a trail of water cells.
 */
export interface Surge {
  /** Column of its front, in cells. */
  x: number;
  readonly dir: 1 | -1;
  /** Ground row under its front (the first static cell). */
  floor: number;
  /** Height in cells: 32 at the start, one less every 12 cells. */
  height: number;
  travelled: number;
  readonly owner: number;
  /** Powder cells it carries along (sand), dropped where it stops. */
  carried: number;
  /** Units already hit (10 damage once each). */
  hit: number[];
  /** Synergy "Vague électrique": it already carried a discharge. */
  electric: boolean;
  /** Synergy "Raz-de-feu": burning oil rides on it. */
  burning: boolean;
  /** A Roomba is swallowing it: it stands still and sinks. */
  eaten?: boolean;
}

/** A flamethrower jet held by a unit (GDD: 1.5 s at most). */
export interface Jet {
  readonly unit: number;
  angle: number;
  /** Ticks of jet left. */
  ticks: number;
}

/**
 * A geyser (GDD section 8): a column of water and steam that throws units up, for 2 s. Put during a turn, it waits for
 * the next one, then `wait` ticks (author's rule).
 */
export interface Geyser {
  readonly x: number;
  /** Ground row it springs from. */
  readonly y: number;
  ticks: number;
  /** Ticks before it springs. */
  wait: number;
  /** Match: the turn it was put in (it starts counting `wait` after it); -1 in the sandbox. */
  readonly turn: number;
}

/** A barrel of barrel.ts: it rolls, leaves a trail, stays BARREL_TURNS turns. */
export interface Barrel {
  /** WEAPON_ACID_BARREL or WEAPON_POWDER_BARREL. */
  readonly weapon: number;
  /** Column (centre) and last row of its box, fixed-point FP. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** The unit that threw it, -1 for the conveyor's. */
  readonly owner: number;
  turns: number;
  /** Sandbox: its age in ticks. */
  tick: number;
  /** Cells left to spill. */
  load: number;
}

/** A Roomba of roomba.ts: it walks, it swallows what is not solid. */
export interface Roomba {
  /** Column (centre) and last row of its box, fixed-point FP. */
  x: number;
  y: number;
  vy: number;
  /** The way it walks and looks. */
  dir: 1 | -1;
  readonly owner: number;
  turns: number;
  /** Sandbox: its age in ticks. */
  tick: number;
  /** It sees something to swallow: the whirlwind is open. */
  sucking: boolean;
  /** Ticks the whirlwind stays open after the last thing seen. */
  sight: number;
  /** It saw matter behind it: it goes back there when there is nothing left ahead. */
  behind: boolean;
  /** The last material it swallowed (the colour of what flies into its mouth). */
  ate: number;
}

/** A pocket volcano (volcano.ts): the cone is terrain, this is what makes it erupt. */
export interface Volcano {
  readonly x: number;
  /** Ground row under the cone. */
  readonly y: number;
  readonly owner: number;
  turns: number;
  /** Sandbox: ticks since its last eruption. */
  tick: number;
}

/**
 * A conveyor belt (Nuclear Plant, the level editor): it carries barrels and units along its surface row, `y` (the first
 * row of ground under their feet).
 */
export interface Belt {
  readonly x0: number;
  readonly x1: number;
  readonly y: number;
  readonly dir: 1 | -1;
  /** Fixed-point FP per tick. */
  readonly speed: number;
  /** It brings acid barrels: one per turn (the Nuclear Plant's). */
  readonly barrels: boolean;
}

/**
 * A jump pad (author's addition, the level editor): a unit that lands or stands on its surface row `y` (the first row of
 * ground under its feet), between columns x0 and x1, is thrown up (unit.ts, PAD_VY) and takes no fall damage there.
 * Static, like the belts: it comes from the scenario, so it is not hashed. It works as long as there is ground to stand on.
 */
export interface JumpPad {
  readonly x0: number;
  readonly x1: number;
  readonly y: number;
}

/** A box of cells, corners included. */
export interface Box { x0: number; y0: number; x1: number; y1: number }

/**
 * The map (GDD section 10): its special rules, fixed by the scenario, and its features with a state.
 * Built by createScenario (maps/index.ts); a plain island has none.
 */
export interface MapState {
  /** Map id (MAP_IDS of maps/index.ts), 'island' or 'custom'. */
  readonly id: string;
  /** Gravity is divided by this: 3 on the Moon (long trajectories, giant jumps, almost no fall damage). */
  readonly gravityDiv: number;
  /**
   * No atmosphere (the Moon): no wind, fire goes out at once, steam freezes (sim), the Wave freezes after 2 s,
   * and a unit thrown above the top of the map is lost in space.
   */
  readonly airless: boolean;
  /** Fort-Pierre: the oil cauldron at the top of the gate tower, until it tips over (then null). */
  cauldron: Box | null;
  /** Fort-Pierre: where the cauldron's oil falls, the gate under it. */
  readonly gate: Box | null;
  /** Fort-Pierre: the powder magazine under the keep, until it blows up (then null). */
  magazine: Box | null;
  /** Île de Braise: the crater, the box of its lava; null on other maps. */
  readonly crater: Box | null;
  /** Île de Braise: turns before the next eruption (it erupts when this reaches 0, then starts again). */
  eruptionIn: number;
  /** Boxes where the water boils into steam, without using up what heats it (Steam Dome, the plant's cooling towers). */
  readonly vents: readonly Box[] | null;
  /** Conveyor belts (the Nuclear Plant's brings acid barrels). */
  readonly belts: readonly Belt[];
  /** Jump pads (the level editor). */
  readonly pads: readonly JumpPad[];
}

export function plainMap(id: string): MapState {
  return { id, gravityDiv: 1, airless: false, cauldron: null, gate: null, magazine: null, crater: null, eruptionIn: 0, vents: null, belts: [], pads: [] };
}

export interface GameState {
  world: World;
  /** The map's rules and features (GDD section 10). */
  map: MapState;
  tick: number;
  units: Unit[];
  projectiles: Projectile[];
  steles: Stele[];
  chests: Chest[];
  mines: Mine[];
  batteries: Battery[];
  barrels: Barrel[];
  roombas: Roomba[];
  volcanoes: Volcano[];
  surges: Surge[];
  jets: Jet[];
  geysers: Geyser[];
  /** Next chest id. */
  nextChest: number;
  /** −10 to +10, changed at the start of every turn (GDD section 3). 0 in the sandbox unless set. */
  wind: number;
  /** Turn-based rules; null in the sandbox, where every command is allowed. */
  match: MatchState | null;
  events: GameEvent[];
  /**
   * The player whose weapon is acting right now (its projectile, mine, wave), -1 otherwise: friendly fire
   * (MatchConfig.friendlyFire). Set and reset within a tick, so not hashed.
   */
  source: number;
}

export function createGameState(world: World, match: MatchState | null = null, map: MapState = plainMap('island')): GameState {
  return { world, map, tick: 0, units: [], projectiles: [], steles: [], chests: [], mines: [], batteries: [], barrels: [], roombas: [], volcanoes: [], surges: [], jets: [], geysers: [], nextChest: 0, wind: 0, match, events: [], source: -1 };
}
